import { create } from 'zustand';
import { generatePresignedUrl, uploadFileToPresignedUrl } from '../services/upload.service';
import { 
  UploadedFile, 
  CreatePreSignedUrlRequest, 
  FileType, 
  FileExtension,
  getFileExtension 
} from '../types/upload.type';

interface UploadState {
  uploadedFile: UploadedFile | null;
  isUploading: boolean;
  error: string | null;
  upload: (file: File) => Promise<void>;
  clear: () => void;
}

export const useUploadStore = create<UploadState>((set, get) => ({
  uploadedFile: null,
  isUploading: false,
  error: null,

  upload: async (file: File) => {
    set({ isUploading: true, error: null });
    try {
      // Validate file extension
      const fileExtension = getFileExtension(file.name);
      if (!fileExtension) {
        throw new Error(`Unsupported file type. Supported types: ${Object.values(FileExtension).join(', ')}`);
      }

      // Step 1: Get presigned URL from backend
      const presignedUrlRequest: CreatePreSignedUrlRequest = {
        original_name: file.name
      };
      
      const presignedUrlResponse = await generatePresignedUrl(
        presignedUrlRequest,
        FileType.CUSTOM,
        fileExtension
      );

      // Step 2: Upload file to presigned URL
      await uploadFileToPresignedUrl(file, presignedUrlResponse.upload_url);

      // Step 3: Create uploaded file object for frontend use
      const uploadedFile: UploadedFile = {
        id: presignedUrlResponse.id,
        original_name: file.name,
        file_type: FileType.CUSTOM,
        file_extension: fileExtension,
        url: presignedUrlResponse.file_path,
        created_at: new Date().toISOString()
      };

      set({ uploadedFile, isUploading: false, error: null });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'File upload failed';
      set({ error: errorMessage, isUploading: false, uploadedFile: null });
      throw error;
    }
  },

  clear: () => {
    set({ uploadedFile: null, error: null });
  },
})); 